<script setup>
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler.js";
import { useToast } from "vue-toast-notification";
import { useRouter } from "vue-router";
import { reactive, ref } from "vue";
import axios from "axios";
import Loader from "../../Components/Loader.vue";
import FormTextField from "../../Components/FormTextField.vue";
import FormTextarea from "../../Components/FormTextarea.vue";
import FormSelectField from "../../Components/FormSelectField.vue";
import FormSwitch from "../../Components/FormSwitch.vue";
import FormDatePicker from "../../Components/FormDatePicker.vue";
import FormSelectCenters from "../../Components/FormSelectCenters.vue";
import FormSelectSearchUsers from "../../Components/FormSelectSearchUsers.vue";
import { useAnnouncementStore } from "../../Store/AnnouncementStore.js";

const { httpErrorsHandler } = useHttpErrorsHandler();
const $toast = useToast();
const router = useRouter();
const announcementStore = useAnnouncementStore();

const loading = ref(false);
const errors = ref(null);

const form = reactive({
    title: null,
    message: null,
    type: 'information',
    is_active: true,
    starts_at: null,
    ends_at: null,
    centers: [],
    users: [],
});

const typeOptions = [
    { value: 'information', text: 'Information (bleu)' },
    { value: 'promotion', text: 'Promotion (vert)' },
    { value: 'important', text: 'Important (orange)' },
    { value: 'urgent', text: 'Urgent (rouge)' },
];

function buildFormData() {
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
        if (key === 'centers' || key === 'users') {
            return (value || []).forEach((item) => formData.append(`${key}[]`, item.id));
        }
        if (typeof value === 'boolean') {
            return formData.append(key, value ? 1 : 0);
        }
        if (value !== null && value !== '') {
            formData.append(key, value);
        }
    });
    return formData;
}

function createAnnouncement() {
    loading.value = true;

    axios.post("/api/announcements", buildFormData())
        .then(() => {
            $toast.success('Bandeau créé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            announcementStore.fetchActiveBanners();
            router.back();
        })
        .catch(error => {
            errors.value = error.response?.data?.errors;
            httpErrorsHandler(error);
            loading.value = false;
        });
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card" v-show="loading">
                <div class="card-body">
                    <loader/>
                </div>
            </div>

            <form v-on:submit.prevent="createAnnouncement" v-show="!loading">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-bullhorn-outline me-2 align-middle fs-4"></i>Créer un bandeau d'annonce</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <form-text-field
                                name="title"
                                label="Titre (optionnel)"
                                :label-hidden="false"
                                @update:field="form.title = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.title"
                                bs-class="col-md-8"
                            />

                            <form-select-field
                                name="type"
                                label="Type"
                                :label-hidden="false"
                                @update:field="form.type = $event"
                                :errors="errors"
                                :required="true"
                                :data="form.type"
                                :liste-options="typeOptions"
                                bs-class="col-md-4"
                            />

                            <form-textarea
                                name="message"
                                label="Message"
                                :label-hidden="false"
                                @update:field="form.message = $event"
                                :errors="errors"
                                :required="true"
                                :data="form.message"
                                bs-class="col-md-12"
                                information-content="Vous pouvez insérer des liens : collez une URL (https://…) ou utilisez le format [texte du lien](https://…)."
                            />

                            <form-select-centers
                                :errors="errors"
                                label="Centres ciblés"
                                name="centers"
                                :required="false"
                                :data="form.centers"
                                @update:field="form.centers = $event"
                                bs-class="col-md-6"
                                :disabled="false"
                                :taggable="false"
                                :label-hidden="false"
                                placeholder="Sélectionnez un ou des centres"
                                information-content="Si aucun centre ni utilisateur n'est sélectionné, le bandeau est affiché à tout le monde."
                            />

                            <form-select-search-users
                                name="users"
                                label="Utilisateurs ciblés"
                                :label-hidden="false"
                                :errors="errors"
                                :required="false"
                                :data="form.users"
                                @update:field="form.users = $event"
                                bs-class="col-md-6"
                                placeholder="Rechercher un utilisateur..."
                                information-content="Recherche par nom (min. 3 caractères)."
                            />

                            <form-date-picker
                                name="starts_at"
                                label="Date de début (optionnel)"
                                :label-hidden="false"
                                @update:field="form.starts_at = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.starts_at"
                                bs-class="col-md-6"
                            />

                            <form-date-picker
                                name="ends_at"
                                label="Date de fin (optionnel)"
                                :label-hidden="false"
                                @update:field="form.ends_at = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.ends_at"
                                bs-class="col-md-6"
                            />

                            <form-switch
                                name="is_active"
                                label="Actif"
                                :label-hidden="false"
                                @update:field="form.is_active = $event"
                                :errors="errors"
                                :data="form.is_active"
                                bs-class="col-md-12"
                            />

                            <div class="d-flex gap-2 justify-content-end">
                                <button type="button" class="btn btn-soft-dark" @click="router.back()">Retour</button>
                                <button type="submit" class="btn btn-secondary">Valider</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped>

</style>
